<script lang="ts">
  /**
   * L'écran Jets : les Compétences, et le journal de ce qui a été lancé.
   *
   * Le calcul, lui, se fait dans le tiroir (voir `TiroirDeJet.svelte`) : il se
   * pose par-dessus la fiche plutôt que de la remplacer, si bien qu'on peut
   * aller vérifier une blessure ou un objet sans perdre le Test en cours.
   */
  import { derniers, type JetJournalise } from '../../donnees/jets'
  import { apercuDesJets } from '../../rules'
  import type { Fiche, PersonnageEnregistre } from '../../rules'

  let {
    vue,
    personnage,
    personnageId,
    ouverte,
    ouvrir,
    version,
  }: {
    vue: Fiche
    personnage: PersonnageEnregistre
    personnageId: string
    /** La Compétence dont le tiroir est ouvert, s'il l'est. */
    ouverte: string | null
    ouvrir: (competence: string) => void
    /** Incrémenté à chaque jet : c'est ce qui fait relire le journal. */
    version: number
  } = $props()

  /* Ce que vaut chaque Compétence une fois appliqué ce que la fiche sait. Le
     chiffre nu restait trompeur : une brûlure à −40 en Séduction ne se voyait
     qu'en ouvrant le tiroir. Seul l'automatique est compté — le reste se
     déclare là-bas, et c'est voulu. */
  const apercus = $derived(new Map(apercuDesJets(personnage).map((a) => [a.competence, a])))

  const familles = $derived(
    vue.caracteristiques.map((carac) => ({
      carac,
      competences: vue.competences.filter((s) => s.carac === carac.id),
    })),
  )

  let journal = $state<JetJournalise[]>([])
  $effect(() => {
    version
    void derniers(personnageId).then((j) => (journal = j))
  })

  const quand = (iso: string) =>
    new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

  const VERDICTS: Record<string, string> = {
    reussite_critique: 'Réussite critique',
    reussite: 'Réussite',
    echec: 'Échec',
    echec_critique: 'Échec critique',
  }
</script>

<p class="chapo">
  Touchez une Compétence : le tiroir rassemblera ce qui pèse sur le Test —
  Statuts, blessures, Capacités conditionnelles, circonstances — avant de lancer
  le dé, sans masquer la fiche.
</p>

<div class="familles">
  {#each familles as f (f.carac.id)}
    <section class="cartouche">
      <div class="bandeau">{f.carac.nom}</div>
      <div class="corps">
        {#each f.competences as c (c.id)}
          {@const a = apercus.get(c.id)}
          <div class="ligne" class:en-cours={ouverte === c.id}>
            <span class="nom">
              {c.nom}
              {#if c.avancee}<span class="badge rang">Classe</span>{/if}
            </span>
            <span class="brute data">{c.brute}</span>
            {#if a && a.automatique !== 0}
              <span
                class="reelle data corrigee"
                class:malus={a.automatique < 0}
                title={a.termes.join(' · ')}
              >
                <s>{c.reelle}</s>
                {a.effective}
              </span>
            {:else}
              <span class="reelle data">{c.reelle}</span>
            {/if}
            <button type="button" class="jet" onclick={() => ouvrir(c.id)}>Jeter</button>
          </div>
        {/each}
      </div>
    </section>
  {/each}
</div>

{#if journal.length > 0}
  <div class="cartouche journal">
    <div class="bandeau">Derniers jets</div>
    <div class="corps">
      <ul class="plates">
        {#each journal as j (j.id)}
          <li class:reussi={j.reussite} class:rate={!j.reussite}>
            <span class="heure data">{quand(j.horodatage)}</span>
            <span class="quoi">{j.nom}</span>
            <span class="calcul data">
              {j.d100} / {j.difficulte}
              {#if j.degats}<span class="marque">{j.degats} dég.</span>{/if}
              {#if j.des}<span class="marque">chanceux</span>{/if}
            </span>
            <span class="issue">{VERDICTS[j.verdict]}</span>
          </li>
        {/each}
      </ul>
      <p class="pied">
        Un jet ne se corrige pas : le journal garde la pile telle qu'elle a été
        appliquée, même si le catalogue change ensuite.
      </p>
    </div>
  </div>
{/if}

<style>
  /* Corrigée par ce que la fiche sait : l'ancienne valeur barrée, la vraie à
     côté. Le détail est dans le survol — et dans le tiroir. */
  .corrigee s {
    opacity: 0.5;
    font-weight: normal;
    margin-right: 0.3em;
  }
  .corrigee.malus {
    color: var(--ko);
  }
  .corrigee:not(.malus) {
    color: var(--ok);
  }
  .chapo {
    color: var(--ink-soft);
    max-width: 62ch;
    margin: 0 0 calc(var(--pas) * 3);
    font-size: 0.9rem;
  }
  .familles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(21rem, 1fr));
    gap: calc(var(--pas) * 3);
  }
  .familles .corps {
    padding: calc(var(--pas) * 2) calc(var(--pas) * 3);
    gap: 0;
  }
  .ligne {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    align-items: center;
    gap: calc(var(--pas) * 2);
    padding: calc(var(--pas) * 1.5) 0;
    border-bottom: 1px solid var(--line);
    font-size: 0.9rem;
  }
  .ligne:last-child {
    border-bottom: none;
  }
  /* La Compétence dont le tiroir est ouvert reste repérable dans la liste. */
  .ligne.en-cours .nom {
    color: var(--gold);
    font-weight: 600;
  }
  .nom {
    display: flex;
    align-items: center;
    gap: var(--pas);
  }
  .nom .badge {
    font-size: 0.55rem;
    padding: 1px 7px;
  }
  .brute {
    color: var(--ink-soft);
    font-size: 0.8rem;
    min-width: 2rem;
    text-align: right;
  }
  .reelle {
    font-size: 1.15rem;
    font-weight: 600;
    min-width: 2.4rem;
    text-align: right;
  }

  .journal {
    margin-top: calc(var(--pas) * 4);
  }
  .journal .plates {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
  }
  .journal li {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    align-items: baseline;
    gap: calc(var(--pas) * 2);
    padding: calc(var(--pas) * 1.5) 0;
    border-bottom: 1px solid var(--line);
    font-size: 0.85rem;
  }
  .journal li:last-child {
    border-bottom: none;
  }
  .heure {
    color: var(--ink-soft);
    font-size: 0.75rem;
  }
  .calcul {
    color: var(--ink-soft);
  }
  .marque {
    font-family: 'Cinzel', serif;
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--gold);
    margin-left: var(--pas);
  }
  .issue {
    font-family: 'Cinzel', serif;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    min-width: 8rem;
    text-align: right;
  }
  .reussi .issue {
    color: var(--ok);
  }
  .rate .issue {
    color: var(--ko);
  }
  .pied {
    font-size: 0.72rem;
    color: var(--ink-soft);
    margin: calc(var(--pas) * 2) 0 0;
  }
</style>
